import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, Star, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../utils/formatters';

const ProjectTimeEntries = ({ projectId, projectName }) => {
  const [entries, setEntries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [opsAssignments, setOpsAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [formData, setFormData] = useState({
    employee_id: '',
    employee_name: '',
    project_id: projectId,
    project_name: projectName,
    hours: '',
    hiring_manager: '',
    hourly_pay_rate: '',
    payment_date: '',
    payment_method: 'cash',
    rating: '5',
    comments: '',
  });

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    try {
      const [entriesRes, employeesRes, assignmentsRes] = await Promise.all([
        api.get('/time-entries'),
        api.get('/employees'),
        api.get(`/ops/assignments?project_id=${projectId}`)
      ]);
      // Filter entries for this specific project
      const projectEntries = entriesRes.data.filter(
        entry => entry.project_id === projectId || entry.project_name === projectName
      );
      setOpsAssignments(assignmentsRes.data || []);
      setEntries(projectEntries);
      setEmployees(employeesRes.data.filter(emp => emp.status === 'active'));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch time entries');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (entry = null) => {
    if (entry) {
      setEditingEntry(entry);
      setFormData({
        employee_id: entry.employee_id,
        employee_name: entry.employee_name,
        project_id: projectId,
        project_name: projectName,
        hours: entry.hours.toString(),
        hiring_manager: entry.hiring_manager,
        hourly_pay_rate: entry.hourly_pay_rate.toString(),
        payment_date: entry.payment_date,
        payment_method: entry.payment_method,
        rating: entry.rating.toString(),
        comments: entry.comments || '',
      });
    } else {
      setEditingEntry(null);
      setFormData({
        employee_id: '',
        employee_name: '',
        project_id: projectId,
        project_name: projectName,
        hours: '',
        hiring_manager: '',
        hourly_pay_rate: '',
        payment_date: '',
        payment_method: 'cash',
        rating: '5',
        comments: '',
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const entryData = {
      ...formData,
      project_id: projectId,
      project_name: projectName,
      hours: parseFloat(formData.hours),
      hourly_pay_rate: parseFloat(formData.hourly_pay_rate),
      rating: parseInt(formData.rating),
    };

    try {
      if (editingEntry) {
        await api.put(`/time-entries/${editingEntry.id}`, entryData);
        toast.success('Time entry updated successfully');
      } else {
        await api.post('/time-entries', entryData);
        toast.success('Time entry logged successfully');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving time entry:', error);
      toast.error('Failed to save time entry');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this time entry?')) return;

    try {
      await api.delete(`/time-entries/${id}`);
      toast.success('Time entry deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting time entry:', error);
      toast.error('Failed to delete time entry');
    }
  };

  const handleEmployeeChange = (employeeId) => {
    const selectedEmployee = employees.find(emp => emp.id === employeeId);
    if (selectedEmployee) {
      setFormData({
        ...formData,
        employee_id: employeeId,
        employee_name: selectedEmployee.name,
        hourly_pay_rate: selectedEmployee.hourly_rate?.toString() || '',
        hiring_manager: selectedEmployee.hiring_manager || '',
      });
    }
  };

  // Filter entries based on selected filter
  const filteredEntries = entries.filter(entry => {
    if (filterEmployee === 'all') return true;
    if (filterEmployee === 'owner') {
      const employee = employees.find(emp => emp.id === entry.employee_id);
      return employee?.type === 'Owner';
    }
    if (filterEmployee === 'employees') {
      const employee = employees.find(emp => emp.id === entry.employee_id);
      return employee?.type !== 'Owner';
    }
    return entry.employee_id === filterEmployee;
  });

  // Calculate totals
  const totalHours = filteredEntries.reduce((sum, e) => sum + (e.hours || 0), 0);
  
  const ownerTotal = filteredEntries
    .filter(e => {
      const emp = employees.find(emp => emp.id === e.employee_id);
      return emp && emp.type === 'Owner';
    })
    .reduce((sum, e) => sum + (e.hours * e.hourly_pay_rate), 0);
  
  const employeeTotal = filteredEntries
    .filter(e => {
      const emp = employees.find(emp => emp.id === e.employee_id);
      return emp && emp.type !== 'Owner';
    })
    .reduce((sum, e) => sum + (e.hours * e.hourly_pay_rate), 0);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    if (dateString.includes('T')) {
      return new Date(dateString).toLocaleDateString();
    } else {
      const [year, month, day] = dateString.split('-');
      return new Date(year, month - 1, day).toLocaleDateString();
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-slate-300'
            }`}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#247C75]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Team & Time</h2>
          <p className="text-slate-600 mt-1">Planned shifts and actual time entries for this project</p>
        </div>
        <Button 
          onClick={() => handleOpenDialog()}
          className="bg-blue-600 hover:bg-blue-700 text-white shadow-md"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Entry
        </Button>
      </div>

      {/* Planned Shifts Section */}
      {opsAssignments.length > 0 && (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Badge className="bg-blue-100 text-blue-700 border-blue-300">
                {opsAssignments.length}
              </Badge>
              Planned Shifts (from Ops Manager)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Planned Hours</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {opsAssignments.map((assignment) => {
                  const employee = employees.find(e => e.id === assignment.employee_id);
                  return (
                    <TableRow key={assignment.id}>
                      <TableCell className="font-medium">
                        {employee?.name || 'Unknown'}
                      </TableCell>
                      <TableCell>
                        {new Date(assignment.date).toLocaleDateString('en-US', { 
                          weekday: 'short', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </TableCell>
                      <TableCell>
                        {assignment.start_time && assignment.end_time
                          ? `${assignment.start_time} - ${assignment.end_time}`
                          : '-'}
                      </TableCell>
                      <TableCell>{assignment.planned_hours}h</TableCell>
                      <TableCell>
                        {assignment.role ? (
                          <Badge variant="outline">{assignment.role}</Badge>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          className={
                            assignment.status === 'completed' ? 'bg-green-100 text-green-700' :
                            assignment.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                            'bg-slate-100 text-slate-700'
                          }
                        >
                          {assignment.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Actual Time Entries Section */}
      <div>
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Actual Time Entries</h3>
      </div>

      {/* Filter */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <Filter className="h-5 w-5 text-slate-400" />
            <Label htmlFor="filter" className="text-sm font-medium">Filter by:</Label>
            <Select value={filterEmployee} onValueChange={setFilterEmployee}>
              <SelectTrigger className="w-[250px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Employees</SelectItem>
                <SelectItem value="owner">Owner Only</SelectItem>
                <SelectItem value="employees">Employees Only</SelectItem>
                {employees
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name} {emp.type === 'Owner' ? '(Owner)' : ''}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {filterEmployee !== 'all' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFilterEmployee('all')}
              >
                Clear Filter
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Time Entries Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle>Time Entries ({filteredEntries.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead>Manager</TableHead>
                  <TableHead>Pay Rate</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Payment Date</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEntries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                      No time entries found. Add your first entry to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEntries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="font-medium">{entry.employee_name}</TableCell>
                      <TableCell>{entry.hours}h</TableCell>
                      <TableCell className="text-sm text-slate-600">{entry.hiring_manager}</TableCell>
                      <TableCell>{formatCurrency(entry.hourly_pay_rate)}/hr</TableCell>
                      <TableCell className="font-semibold text-emerald-600">
                        {formatCurrency(entry.hours * entry.hourly_pay_rate)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDate(entry.payment_date)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {entry.payment_method.replace(/_/g, ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>{renderStars(entry.rating)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(entry)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(entry.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          
          {/* Subtotals */}
          <div className="mt-6 pt-4 border-t">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Pay Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="text-xs text-purple-600 font-medium mb-1">Total Hours</div>
                <div className="text-2xl font-bold text-purple-700">
                  {totalHours.toFixed(2)}h
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="text-xs text-blue-600 font-medium mb-1">Owner Earnings</div>
                <div className="text-2xl font-bold text-blue-700">
                  {formatCurrency(ownerTotal)}
                </div>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <div className="text-xs text-slate-600 font-medium mb-1">Employee Labour Cost</div>
                <div className="text-2xl font-bold text-slate-700">
                  {formatCurrency(employeeTotal)}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dialog Form */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEntry ? 'Edit Time Entry' : 'Log Time Entry'}</DialogTitle>
            <DialogDescription>
              {editingEntry ? 'Update time entry details' : 'Add a new time entry for this project'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Employee */}
              <div className="space-y-2">
                <Label htmlFor="employee">Employee *</Label>
                <Select
                  value={formData.employee_id}
                  onValueChange={handleEmployeeChange}
                  required
                >
                  <SelectTrigger id="employee">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees
                      .sort((a, b) => {
                        // Sort owners first, then alphabetically
                        if (a.type === 'Owner' && b.type !== 'Owner') return -1;
                        if (a.type !== 'Owner' && b.type === 'Owner') return 1;
                        return a.name.localeCompare(b.name);
                      })
                      .map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.name} - {formatCurrency(emp.hourly_rate)}/hr ({emp.type})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Hours */}
              <div className="space-y-2">
                <Label htmlFor="hours">Hours *</Label>
                <Input
                  id="hours"
                  type="number"
                  step="0.25"
                  min="0"
                  value={formData.hours}
                  onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                  placeholder="8.0"
                  required
                />
              </div>

              {/* Payment Date */}
              <div className="space-y-2">
                <Label htmlFor="payment_date">Date *</Label>
                <Input
                  id="payment_date"
                  type="date"
                  value={formData.payment_date}
                  onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                  required
                />
              </div>

              {/* Hourly Pay Rate */}
              <div className="space-y-2">
                <Label htmlFor="hourly_pay_rate">Hourly Pay Rate *</Label>
                <Input
                  id="hourly_pay_rate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.hourly_pay_rate}
                  onChange={(e) => setFormData({ ...formData, hourly_pay_rate: e.target.value })}
                  placeholder="25.00"
                  required
                />
              </div>

              {/* Hiring Manager */}
              <div className="space-y-2">
                <Label htmlFor="hiring_manager">Hiring Manager</Label>
                <Input
                  id="hiring_manager"
                  value={formData.hiring_manager}
                  onChange={(e) => setFormData({ ...formData, hiring_manager: e.target.value })}
                  placeholder="Manager name"
                />
              </div>

              {/* Payment Method */}
              <div className="space-y-2">
                <Label htmlFor="payment_method">Payment Method</Label>
                <Select
                  value={formData.payment_method}
                  onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
                >
                  <SelectTrigger id="payment_method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="e-transfer">E-Transfer</SelectItem>
                    <SelectItem value="direct_deposit">Direct Deposit</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Rating */}
              <div className="space-y-2">
                <Label htmlFor="rating">Rating (1-5)</Label>
                <Select
                  value={formData.rating}
                  onValueChange={(value) => setFormData({ ...formData, rating: value })}
                >
                  <SelectTrigger id="rating">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5].map(num => (
                      <SelectItem key={num} value={num.toString()}>
                        {'⭐'.repeat(num)} ({num})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Comments - Full width */}
            <div className="space-y-2">
              <Label htmlFor="comments">Notes</Label>
              <Textarea
                id="comments"
                value={formData.comments}
                onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                placeholder="Add any notes about this time entry..."
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                className="bg-[#247C75] hover:bg-[#1f6962] text-white"
              >
                {editingEntry ? 'Update Entry' : 'Log Time'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectTimeEntries;
